import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'

import {
  fetchInvoice,
  fetchInvoicePayments,
  fetchInvoices,
  fetchInvoiceSummary,
  type InvoiceFilters,
} from '#admin/features/billing/api.ts'
import { fetchCreditNotes } from '#admin/features/billing/credit-notes-api.ts'
import { fetchExchangeRates } from '#admin/features/billing/exchange-rates-api.ts'
import { fetchHistory } from '#admin/features/billing/history.ts'
import {
  fetchAdminPaymentMethods,
  fetchSelectablePaymentMethods,
} from '#admin/features/billing/payment-methods-api.ts'
import {
  fetchCompanies,
  fetchPayment,
  fetchPayments,
  type PaymentFilters,
} from '#admin/features/billing/payments-api.ts'

/**
 * Query options, not hooks, so a route loader can prefetch with the same key and fetcher the
 * component reads. Everything sits under `['billing']`: one write anywhere in the ledger moves
 * figures everywhere else (a payment settles an invoice, a credit note reduces one), so a
 * mutation invalidates the whole subtree rather than guessing which screens it touched.
 */
export const BILLING_KEY = ['billing'] as const

export const invoicesQuery = (filters: InvoiceFilters) =>
  infiniteQueryOptions({
    queryKey: [...BILLING_KEY, 'invoices', filters],
    queryFn: ({ pageParam, signal }) => fetchInvoices(filters, pageParam, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })

export const invoiceSummaryQuery = () =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'summary'],
    queryFn: ({ signal }) => fetchInvoiceSummary(signal),
  })

export const invoiceQuery = (id: string) =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'invoice', id],
    queryFn: ({ signal }) => fetchInvoice(id, signal),
  })

export const invoicePaymentsQuery = (id: string) =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'invoice', id, 'payments'],
    queryFn: ({ signal }) => fetchInvoicePayments(id, signal),
  })

/** Open invoices to allocate to — the first page, searched by the dialog. */
export const openInvoicesQuery = (q: string) =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'invoices', 'open', q],
    queryFn: ({ signal }) => fetchInvoices({ q, status: '' }, undefined, { signal, limit: 30 }),
  })

export const creditNotesQuery = (invoiceId?: string) =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'credit-notes', invoiceId ?? 'all'],
    queryFn: ({ signal }) => fetchCreditNotes(invoiceId, signal),
  })

export const paymentsQuery = (filters: PaymentFilters) =>
  infiniteQueryOptions({
    queryKey: [...BILLING_KEY, 'payments', filters],
    queryFn: ({ pageParam, signal }) => fetchPayments(filters, pageParam, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })

export const companiesQuery = () =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'companies'],
    queryFn: ({ signal }) => fetchCompanies(signal),
  })

export const paymentQuery = (id: string) =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'payment', id],
    queryFn: ({ signal }) => fetchPayment(id, signal),
  })

/** Under the record's own key, so any write that refreshes the invoice or payment refreshes it too. */
export const invoiceHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'invoice', id, 'history'],
    queryFn: ({ signal }) => fetchHistory('invoices', id, signal),
  })

export const paymentHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'payment', id, 'history'],
    queryFn: ({ signal }) => fetchHistory('payments', id, signal),
  })

export const paymentMethodsQuery = () =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'payment-methods', 'admin'],
    queryFn: ({ signal }) => fetchAdminPaymentMethods(signal),
  })

/** The methods a payer can select. Under `['billing']`, so turning one on refreshes it too. */
export const selectablePaymentMethodsQuery = () =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'payment-methods', 'selectable'],
    queryFn: ({ signal }) => fetchSelectablePaymentMethods(signal),
  })

/** The exchange rates of this edition. The invoice dialog reads it to fill in a rate. */
export const exchangeRatesQuery = () =>
  queryOptions({
    queryKey: [...BILLING_KEY, 'exchange-rates'],
    queryFn: ({ signal }) => fetchExchangeRates(signal),
  })
